import type { Insight } from './insightTypes';
import type { CoachingInsight, FrictionMoment } from '../types/api';

/**
 * Bridge adapter: map a chapter's existing per-chapter signals — friction
 * Moments ("how it went") and coaching insights ("how efficiently it ran") —
 * into the typed `Insight[]` the FlaggedInsights panel renders (#601).
 *
 * FlaggedInsights is a pure presentational component that receives `Insight[]`;
 * the dedicated backend source that emits the full five-type contract
 * (observation / recommendation / decision / anomaly / win) is a sibling ticket.
 * Until it lands, this adapter lets the redesigned panel render on real sessions
 * from the data we already compute:
 *   - a friction Moment (a correction or a stuck/looping stretch) → `anomaly`
 *   - a coaching insight (which carries an explicit recommendation) → `recommendation`
 *
 * Ordering follows the inputs: friction first (what went wrong), then coaching
 * (how to run tighter). Returns [] when the chapter has neither.
 */
export function toFlaggedInsights(
  coaching: CoachingInsight[] | undefined,
  friction: FrictionMoment[] | undefined,
): Insight[] {
  const out: Insight[] = [];

  for (const [i, f] of (friction ?? []).entries()) {
    out.push({
      id: `friction-${i}`,
      type: 'anomaly',
      title: f.description,
      body: f.quote ? `“${f.quote}”` : 'A correction or stuck stretch was flagged in this chapter.',
    });
  }

  for (const [i, c] of (coaching ?? []).entries()) {
    out.push({
      id: `coaching-${c.type}-${i}`,
      type: 'recommendation',
      title: c.title,
      body: c.recommendation || c.detail,
    });
  }

  return out;
}
